import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { IEventMetadata, IFormFieldMetadata, IFormMetadata, IFormSectionMetadata, IFormVersionMetadata, ISourceData, ISourceDataConfiguration, ISourceDataConfigurationEvent, ISourceDataConfigurationForm, ISourceDataConfigurationFormSection, ISourceDataConfigurationFormVersion, ISourceDataEvent, ISourceDataField, ISourceDataForm, ITrialFormMetadata, SourceDataPriority } from '@/api/dtos';
import { BasicAlert, CollapsiblePanel, ICollapsiblePanelComponentProps, Markdown } from '@ngt/forms';
import { alpha, Box, Button, Chip, Divider, Paper, styled, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faCodeBranch, faRepeat, faSquareList } from '@fortawesome/duotone-light-svg-icons';
import { faCheckCircle, faExclamationCircle, faExclamationTriangle, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { faExclamationTriangle as faExclamationTriangleSolid, faTriangle } from '@fortawesome/pro-solid-svg-icons';
import { ISourceDataFormFormComponentProps } from '@/utilities/forms/SourceDataFormProcessor';

const Title = styled(Typography)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: theme.spacing(4, 2, 4, 2),
    margin: theme.spacing(0)
}));

const IconContainer = styled(Box)(({ theme }) => ({
    marginRight: theme.spacing(2)
}));

const ExtraContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    marginLeft: 'auto',
    alignItems: 'center'
}));

const PriorityContainer = styled(Box)(({ theme }) => ({
    marginRight: theme.spacing(2)
}));

const RepeatChip = styled(Chip)(({ theme }) => ({
}));

const Content = styled(Paper)(({ theme }) => ({
}));

const SourceDataFormFormTitled: FC<ISourceDataFormFormComponentProps> = ({
    formLabel,
    formVersionLabel,
    eventMetadata,
    formVersionEnabled,
    formConfiguration,
    formSourceData,
    settings,
    children
}) => {   
    
    const hasForm = !!formSourceData;

    if (!hasForm && settings?.hideForms) {
        return null;
    }

    if (hasForm && !formVersionEnabled && settings?.hideVersions) {
        return null;
    }

    return (
        <>
            <Title variant="h3">
                <IconContainer>
                    <FontAwesomeIcon icon={faSquareList} />
                </IconContainer>
                {formLabel}
                <ExtraContainer>
                    {
                        formSourceData?.priority === SourceDataPriority.Low && (
                            <Tooltip title="Priority: Low">
                                <PriorityContainer sx={{color: 'success.main', transform: 'rotate(180deg)'}}>
                                    <FontAwesomeIcon icon={faTriangle} />
                                </PriorityContainer>
                            </Tooltip>
                        )
                    }
                    {
                        formSourceData?.priority === SourceDataPriority.High && (
                            <Tooltip title="Priority: High">
                                <PriorityContainer sx={{color: 'warning.main'}}>
                                    <FontAwesomeIcon icon={faTriangle} />
                                </PriorityContainer>
                            </Tooltip>
                        )
                    }
                    {
                        formSourceData?.priority === SourceDataPriority.Critical && (
                            <Tooltip title="Priority: Critical">
                                <PriorityContainer sx={{color: 'error.main'}}>
                                    <FontAwesomeIcon icon={faExclamationTriangleSolid} />
                                </PriorityContainer>
                            </Tooltip>
                        )
                    }
                    {
                        formVersionLabel && (
                            <Tooltip title="Version">
                                <RepeatChip
                                    icon={<FontAwesomeIcon icon={faCodeBranch} />}
                                    label={formVersionLabel}
                                />
                            </Tooltip>
                        )
                    }
                </ExtraContainer>
            </Title>
            <Content>
                {
                    !hasForm && (
                        <BasicAlert
                            title="No Recorded Form"
                            description="The form has not yet been recorded in OpenClinica."
                            severity="info"
                            icon={faInfoCircle}
                        />
                    )
                }
                {
                    hasForm && !formVersionEnabled && (
                        <BasicAlert
                            title="Version Missing Configuration"
                            description="The version of the form in OpenClinica does not have a configured for Source Data Verification."
                            severity="warning"
                            icon={faExclamationTriangle}
                        />
                    )
                }
                {
                    hasForm && formVersionEnabled && (
                        <>
                            { 
                                !!formConfiguration?.alertInformation && (
                                    <BasicAlert
                                        description={<Markdown.Preview>{formConfiguration.alertInformation}</Markdown.Preview>}
                                        severity="info"
                                        icon={faInfoCircle}
                                    />
                                )
                            }
                            { 
                                !!formConfiguration?.alertWarning && (
                                    <BasicAlert
                                        description={<Markdown.Preview>{formConfiguration.alertWarning}</Markdown.Preview>}
                                        severity="warning"
                                        icon={faExclamationCircle}
                                    />
                                )
                            }
                            { 
                                !!formConfiguration?.alertError && (
                                    <BasicAlert
                                        description={<Markdown.Preview>{formConfiguration.alertError}</Markdown.Preview>}
                                        severity="error"
                                        icon={faExclamationTriangle}
                                    />
                                )
                            }
                            { 
                                !!formConfiguration?.alertSuccess && (
                                    <BasicAlert
                                        description={<Markdown.Preview>{formConfiguration.alertSuccess}</Markdown.Preview>}
                                        severity="success"
                                        icon={faCheckCircle}
                                    />
                                )
                            }
                            {children}
                        </>
                    )
                }
            </Content>
        </>
    );
}

export default SourceDataFormFormTitled;